/** @jsxRuntime classic */
/** @jsx jsx */
import * as React from 'react';
import {Dispatch, SetStateAction, useCallback, useEffect, useReducer, useRef, useState} from 'react';
import {Button, InfoBox} from '@flexe/ui-components';
import {pick} from 'lodash';
import {addDays} from 'date-fns';
import {css, jsx} from '@emotion/react';
import HeaderDetail from '../../shared/HeaderDetail';
import HeaderEditableDetail, {HeaderEditableDetailType} from '../../shared/HeaderEditableDetail';
import {CancelInboundModal} from '../../dropoffs/shipper/CancelInboundModal';
import {formatDate} from '../../shared/utilities/DataFormatting';
import {Reservation} from '../../shared/CommonInterfaces';
import ContainersService from '../../shared/services/ContainersService';
import {ContainerDeliveryState, InboundStatusMap} from '../../dropoffs/shared/DropoffInterfaces';
import {useInfoBox} from '../../dropoffs/shared/UseInfoBox';
import {Inbound, InboundCapturableProperty} from './InboundInterfaces';
import InboundNotes from './InboundNotes';
import InboundErrors from './InboundErrors';

export interface InboundDetailsHeaderPanelProps {
  containersService: ContainersService;
  enableInboundPackaging: boolean;
  errors: string[];
  handleRefreshPage: () => void;
  inbound: Inbound;
  inputTextMap: Map<string, string>;
  isEditMode: boolean;
  isEditing: boolean;
  isShipper: boolean;
  onInputTextMap: (
    eventTargetValue?: string,
    propertyKey?: string,
    inputTextMap?: Map<string, string>,
    setInputTextMap?: (value: any) => void
  ) => void;
  onUpdateInstructions: (instructions: string) => void;
  reservation: Reservation;
  shipperEditProps?: {
    onCancelEditClicked: () => void;
    onDeleteDocs: () => void;
    setInbound: Dispatch<SetStateAction<Inbound>>;
    toggleEditMode: () => void;
  };
}

const VALUE = 'value';
const inboundHeaderStyle = css({
  maxWidth: 3000
});

export const InboundDetailsHeaderPanel: React.FC<InboundDetailsHeaderPanelProps> = (props) => {
  const {
    containersService,
    enableInboundPackaging,
    errors,
    handleRefreshPage,
    inbound,
    inputTextMap,
    isEditMode,
    isEditing,
    isShipper,
    onInputTextMap,
    onUpdateInstructions,
    reservation,
    shipperEditProps: {onCancelEditClicked, onDeleteDocs, setInbound, toggleEditMode} = {}
  } = props;
  const [documentErrors, setDocumentErrors] = useState<string[]>([]);
  const [isCancelModalOpen, toggleIsCancelModalOpen] = useReducer((isOpen) => !isOpen, false);

  const originalFormValues = pick(inbound, 'moveDate', 'sealNumber', 'trailerNumber');
  const [formValues, setFormValues] = useState(originalFormValues);
  const infoBoxHook = useInfoBox();

  const isInboundActive = [
    ContainerDeliveryState.new,
    ContainerDeliveryState.confirmed,
    ContainerDeliveryState.altered,
    ContainerDeliveryState.arrived
  ].includes(inbound.state as ContainerDeliveryState);

  const originalJson = JSON.stringify(originalFormValues);
  useEffect(() => {
    setFormValues(JSON.parse(originalJson));
  }, [originalJson]);

  const handleUpdate = useCallback(async () => {
    if (!isEditMode) {
      toggleEditMode();
      return;
    }

    const response = await containersService.updateContainerDelivery({
      data: formValues,
      id: inbound.id
    });
    onDeleteDocs();
    if (response?.errors?.length > 0) {
      setFormValues(originalFormValues);
      infoBoxHook.setError(response.errors.map((_) => _.detail).join(', '));
    } else {
      setInbound((_) => ({..._, ...formValues}));
      infoBoxHook.setInfo('Successfully updated inbound.');
    }

    toggleEditMode();
  }, [
    isEditMode,
    toggleEditMode,
    containersService,
    inbound,
    formValues,
    onDeleteDocs,
    originalFormValues,
    infoBoxHook,
    setFormValues
  ]);

  const hasExtraProperties = (): boolean => {
    return (
      Object.keys(inbound.providedProperties || {}).length > 0 ||
      Object.keys(inbound.capturableProperties || {}).length > 0
    );
  };

  const inboundStatus = InboundStatusMap.get(inbound.state);

  return (
    <div className="inbound-details-background">
      <div id="inbound-details-container" className="container-fluid" css={inboundHeaderStyle}>
        <div id="inbound-details">
          <Navigation inboundLink={isShipper ? '/s/dropoffs' : '/wh/dropoffs'} inboundId={inbound.id} />
          <InboundErrors errors={errors} />
          {documentErrors?.map((err, i) => (
            <InfoBox infoType={'error'} info={err} key={i} />
          ))}
          {infoBoxHook.component}
          <header className="inbound-header">
            <div>
              <h1 className="inbound-title">Inbound #{inbound.id}</h1>
              <span id="inbound-status" className={inboundStatus.className}>
                {inboundStatus.text}
              </span>
            </div>
            {isShipper && isInboundActive && (
              <div className="shipper-button-container">
                <div className="shipper-cancel-edit-button">
                  {isEditMode && (
                    <Button testid={'edit-cancel-button'} onPress={onCancelEditClicked}>
                      Cancel
                    </Button>
                  )}
                </div>
                <div className="shipper-edit-update-button">
                  <Button onPress={handleUpdate} visualType={isEditMode ? 'primary' : 'secondary'}>
                    {isEditMode ? 'Update' : 'Edit'}
                  </Button>
                </div>
                <div className="shipper-cancel-button">
                  <Button onPress={toggleIsCancelModalOpen}>Cancel Inbound</Button>
                  <CancelInboundModal
                    containerDeliveryId={inbound.id}
                    isOpen={isCancelModalOpen}
                    toggleModal={toggleIsCancelModalOpen}
                    containersService={containersService}
                    handleRefreshPage={handleRefreshPage}
                  />
                </div>
              </div>
            )}
          </header>
          <div id="details">
            <div id="reservation-info">
              <span>{`Reservation >> ${reservation.id}`}</span>
            </div>
          </div>
          <div id="inbound-information">
            <div className="col-md-4">
              <h4 className="text-uppercase">Inbound Information</h4>
              {!isShipper && inbound.completedBy && (
                <div className="detail-field">
                  <span className="identifier">Completed By: </span>
                  {inbound.completedBy}
                </div>
              )}
              {isShipper && !enableInboundPackaging && <HeaderDetail label={'Type:'} value={'Inbound Unload'} />}
              {isShipper ? (
                <HeaderEditableDetail
                  isEditing={
                    isEditMode &&
                    [
                      ContainerDeliveryState.new,
                      ContainerDeliveryState.confirmed,
                      ContainerDeliveryState.altered
                    ].includes(inbound.state as ContainerDeliveryState)
                  }
                  label={'Date:'}
                  onChange={(moveDate: any) => setFormValues((_) => ({..._, moveDate}))}
                  required
                  type={{
                    displayDateFormat: 'MM/DD/YYYY',
                    type: HeaderEditableDetailType.date
                  }}
                  validations={{
                    min: formatDate(addDays(new Date(), 1), 'MM/DD/YYYY')
                  }}
                  value={formValues.moveDate}
                />
              ) : (
                <HeaderDetail
                  label={'Requested:'}
                  value={formatDate(new Date(inbound.moveDate), 'MM/DD/YYYY', 'UTC')}
                />
              )}
              {enableInboundPackaging &&
                inbound.expectedInboundPackaging &&
                inbound.expectedInboundPackaging !== '' && (
                  <HeaderDetail label={'Expected Inbound Type:'} value={inbound.expectedInboundPackaging} />
                )}
              {enableInboundPackaging && (
                <HeaderDetail label={'Actual Inbound Type:'} value={inbound.actualInboundPackaging} />
              )}
              <HeaderEditableDetail
                isEditing={isEditMode && isInboundActive}
                label={'Trailer #:'}
                onChange={(trailerNumber: any) => setFormValues((_) => ({..._, trailerNumber}))}
                required
                type={HeaderEditableDetailType.text}
                value={formValues.trailerNumber}
              />
              <HeaderEditableDetail
                isEditing={isEditMode && isInboundActive}
                label={'Seal #:'}
                onChange={(sealNumber: any) => setFormValues((_) => ({..._, sealNumber}))}
                required
                type={HeaderEditableDetailType.text}
                value={formValues.sealNumber}
                data-testid="seal-number-input"
              />
              {inbound.receivingWindow.startAt && !isShipper && (
                <HeaderDetail
                  label={'Inbound Window:'}
                  value={inbound.receivingWindow.startAt + ' - ' + inbound.receivingWindow.endAt}
                />
              )}
              {inbound.requireReferenceId && <HeaderDetail label={'Has LPNs with Ref. ID:'} value={'Yes'} />}
              <div className="vertical-rule" />
            </div>
            {hasExtraProperties() && (
              <div data-testid="inbound-extra-properties" className="col-md-4">
                <h4 className="text-uppercase">Additional Information</h4>
                {inbound.providedProperties &&
                  Object.entries(inbound.providedProperties).map((value, i) => (
                    <HeaderDetail key={i} label={`${value[0]}: `} value={value[1]} />
                  ))}
                {inbound.capturableProperties &&
                  Object.entries(inbound.capturableProperties).map(
                    ([name, property]: [string, InboundCapturableProperty], i) => (
                      <div>
                        <HeaderEditableDetail
                          editingInstructions={property.instructions}
                          isEditing={isEditing}
                          key={i}
                          propertyKey={name}
                          label={`${property.displayLabel}: `}
                          maxLength={50}
                          onChange={onInputTextMap}
                          required={property.required}
                          toolTipId={`inbound-details-input-tooltip-${i}`}
                          type={HeaderEditableDetailType[property.type]}
                          value={inputTextMap.get(name) || inbound.capturableProperties?.[name]?.[VALUE]}
                        />
                      </div>
                    )
                  )}
                <div className="vertical-rule" />
              </div>
            )}
            <div className="col-md-4">
              <h4 className="text-uppercase">Notes</h4>
              <div className="inbound-handling-instructions">
                <span className="identifier">
                  <strong>Inbound Handling Instruction: </strong>
                </span>
                <InboundNotes
                  isShipper={isShipper}
                  containersService={containersService}
                  inbound={inbound}
                  onUpdateInstructions={onUpdateInstructions}
                  onDocumentUploadError={setDocumentErrors}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Navigation: React.FC<{
  inboundLink: string;
  inboundId: Inbound['id'];
}> = ({inboundId, inboundLink}) => (
  <div className="breadcrumbs inbound">
    <a href={inboundLink}>Inbounds</a>
    <span className="navigation-separator"> / </span>
    <span>Inbound {inboundId}</span>
  </div>
);

export default InboundDetailsHeaderPanel;
